import {
  BookingStatusChip,
  DataTable,
  ErrorState,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ColumnDef,
} from '@zuruflex/ui';
import { formatDateTimeIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import { BOOKING_STATUSES, type Booking } from '@zuruflex/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTableLabels } from '../lib/table-labels.js';

const ANY = 'any';
/** Not a status: hand-sent MoMo the customer says they paid, awaiting approval. */
const CLAIMED = 'claimed';

export function BookingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const [status, setStatus] = useState(ANY);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bookings', { status }],
    // The API pages server-side; the table shows one page at a time.
    queryFn: () =>
      api.listBookings({
        perPage: 50,
        ...(status === CLAIMED ? { claimed: 'true' as const } : status !== ANY ? { status } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<Booking>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: t('common:field.reference'),
        cell: ({ row }) => <span className="font-mono font-semibold">{row.original.reference}</span>,
      },
      {
        accessorKey: 'status',
        header: t('common:field.status'),
        cell: ({ row }) => (
          <BookingStatusChip
            status={row.original.status}
            label={t(`common:bookingStatus.${row.original.status}`)}
          />
        ),
      },
      {
        accessorKey: 'pickupAt',
        header: t('common:field.pickUp'),
        cell: ({ row }) => formatDateTimeIn(row.original.pickupAt, locale),
      },
      {
        accessorKey: 'dropoffAt',
        header: t('common:field.dropOff'),
        cell: ({ row }) => formatDateTimeIn(row.original.dropoffAt, locale),
      },
      { accessorKey: 'days', header: t('common:field.days') },
      {
        accessorKey: 'total',
        header: t('common:field.total'),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums">
            {formatMoneyIn(row.original.total, row.original.currency, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <>
      <PageHeader
        title={t('bookings.title')}
        description={data ? t('bookings.count', { count: data.total }) : t('bookings.loading')}
      />

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          labels={tableLabels}
          columns={columns}
          data={data?.items ?? []}
          isLoading={isPending}
          searchColumn="reference"
          searchPlaceholder={t('bookings.searchPlaceholder')}
          onRowClick={(row) => navigate(`/bookings/${row.reference}`)}
          emptyMessage={t('bookings.noMatch')}
          toolbar={
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('bookings.allStatuses')}</SelectItem>
                <SelectItem value={CLAIMED}>{t('bookings.momo.filter')}</SelectItem>
                {BOOKING_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`common:bookingStatus.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
      )}
    </>
  );
}
